import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from './config';
import PlayerSearchDropdown from './PlayerSearchDropdown';
import BottomNavigation from './BottomNavigation';
import { MemberBrand, MemberIcon, MemberCard, MemberButton, MemberPage, useMemberTheme } from './MemberUI';
import '../css/MatchRequest.css';
import '../css/MatchRequestPremium.css';
const storedUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); }
  catch { return null; }
};

function MatchRequest() {
  useMemberTheme();
  const navigate = useNavigate();
  const user = storedUser();
  const groupId = Number(localStorage.getItem('last_group_id') || user?.group_id || 0);
  const userId = Number(user?.user_id || 0);
  const [mode, setMode] = useState(2);
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const requestInFlight = useRef(false);

  useEffect(() => {
    if (!userId) { navigate('/?redirect=%2Fmatch-request', { replace: true }); return; }
    if (!groupId) { setLoading(false); setError('ไม่พบก๊วนที่กำลังใช้งาน กรุณาเช็กอินเล่นวันนี้ก่อน'); return; }
    fetch(`${API_BASE}/api/match-planner/${groupId}/request-options`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
    }).then(async response => {
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'โหลดรายชื่อไม่สำเร็จ');
      setPlayers(result.players || []);
    }).catch(requestError => setError(requestError.message)).finally(() => setLoading(false));
  }, [groupId, userId, navigate]);

  const requester = useMemo(() => players.find(player => Number(player.user_id) === userId), [players, userId]);
  const selectable = players.filter(player => Number(player.user_id) !== userId);
  const slotCount = mode - 1;

  const changeMode = nextMode => {
    if (saving) return;
    setMode(nextMode);
    setSelected(current => current.slice(0, nextMode - 1));
    setMessage(''); setError('');
    setConfirmed(false);
  };

  const updateSlot = (index, player) => {
    if (saving) return;
    if (player && (Number(player.user_id) === userId || selected.some((item, slot) => slot !== index && Number(item?.user_id) === Number(player.user_id)))) { setError('เลือกผู้เล่นซ้ำไม่ได้'); return; }
    setConfirmed(false);
    setSelected(current => {
      const next = [...current]; next[index] = player; return next;
    });
  };

  const submit = async event => {
    event.preventDefault();
    if (requestInFlight.current) return;
    if (!confirmed) { setError('กรุณายืนยันว่าตรวจสอบรายชื่อและคุยกับผู้เล่นแล้ว'); return; }
    if (selected.filter(Boolean).length !== slotCount) { setError(`กรุณาเลือกผู้เล่นอีก ${slotCount} คนให้ครบ`); return; }
    if (new Set([userId, ...selected.map(player => Number(player.user_id))]).size !== mode) { setError('เลือกผู้เล่นซ้ำไม่ได้'); return; }
    requestInFlight.current = true;
    setSaving(true); setError(''); setMessage('');
    try {
      const response = await fetch(`${API_BASE}/api/match-planner/${groupId}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ mode, playerIds: selected.map(player => player.user_id), note })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'ส่งคำขอไม่สำเร็จ');
      setMessage(mode === 2 ? 'ส่งคำขอคู่ 2 คนแล้ว กรุณารอผู้จัดตรวจสอบและเรียกตามคิว' : 'ส่งคำขอจัดเกม 4 คนแล้ว กรุณารอผู้จัดอนุมัติก่อนเข้าคิว');
      setSelected([]); setNote(''); setConfirmed(false);
    } catch (requestError) { setError(requestError.message); }
    finally { requestInFlight.current = false; setSaving(false); }
  };

  return <MemberPage page="request" as="div" className="mr-page">
    <MemberBrand><header className="mr-page-heading"><h1>จัดแมตช์เอง</h1><p>เลือกรายชื่อแล้วส่งให้ผู้จัดตรวจสอบ</p></header></MemberBrand>
    <main className="mr-shell">
      <MemberCard className="mr-explain"><h2><MemberIcon name="info" /> ผู้เล่นจัดแมตช์ได้อย่างไร?</h2><p><b>คำขอจะยังไม่เข้าคิวทันที</b> ผู้จัดจะตรวจสอบความเหมาะสม สถานะผู้เล่น และเรียกตามลำดับคิว</p><p>กรุณาตรวจชื่อให้ถูกต้อง และคุยยืนยันกับผู้เล่นทุกคนที่เลือกก่อนส่งคำขอ เพื่อป้องกันการเลือกผิดคนหรืออีกฝ่ายยังไม่พร้อม</p></MemberCard>
      <MemberCard as="form" className="mr-form" onSubmit={submit} aria-busy={saving}>
        <h2>เลือกจำนวนผู้เล่น</h2>
        <div className="mr-modes"><button type="button" aria-pressed={mode===2} disabled={saving} className={mode===2?'active':''} onClick={() => changeMode(2)}><b><MemberIcon name="users" /> จัดแบบ 2 คน</b><span>ส่งคู่ผู้เล่นเพื่อรอจัดเกม</span></button><button type="button" aria-pressed={mode===4} disabled={saving} className={mode===4?'active':''} onClick={() => changeMode(4)}><b><MemberIcon name="court" /> จัดแบบ 4 คน</b><span>เสนอผู้เล่นครบ 4 คนเพื่อรอผู้จัดอนุมัติ</span></button></div>
        <div className="mr-owner"><span>ผู้ส่งคำขอ</span><b>{requester?.name || user?.name || 'คุณ'}</b></div>
        {loading ? <p className="mr-muted" role="status">กำลังโหลดรายชื่อ…</p> : Array.from({length:slotCount}, (_, index) => <div className="mr-slot" key={`${mode}-${index}`}><span>{mode===4 ? `ผู้เล่นคนที่ ${index+2}${index===0?' · คู่ของคุณ':''}` : 'ผู้เล่นที่ต้องการจับคู่'}</span><PlayerSearchDropdown value={selected[index] || null} allPlayers={saving ? [] : selectable} placeholder={`   ค้นหาผู้เล่นคนที่ ${index+2}`} usedIds={selected.filter(Boolean).map(player => player.user_id).filter(id => Number(id)!==Number(selected[index]?.user_id))} onChange={player => updateSlot(index, player)} />{selected[index] && <div className="mr-selection"><span><MemberIcon name="profile" size={18} /> {selected[index].name}</span><button type="button" disabled={saving} aria-label={`นำ ${selected[index].name} ออก`} onClick={() => updateSlot(index, null)}><MemberIcon name="close" size={18} /></button></div>}</div>)}
        <label className="mr-note"><span>หมายเหตุถึงผู้จัด (ไม่บังคับ)</span><textarea value={note} maxLength={255} onChange={event => setNote(event.target.value)} placeholder="เช่น ทุกคนยืนยันแล้ว / ขอเล่นรอบถัดไป" /></label>
        <label className="mr-confirm"><input type="checkbox" required checked={confirmed} disabled={saving} onChange={event => setConfirmed(event.target.checked)} /><span>ฉันตรวจสอบชื่อและยืนยันกับผู้เล่นที่เลือกแล้ว</span></label>
        {error && <p className="mr-error" role="alert">{error}</p>}{message && <p className="mr-success" role="status">{message}</p>}
        <MemberButton type="submit" className="mr-submit" busy={saving} disabled={loading || !groupId || !confirmed || selected.filter(Boolean).length !== slotCount}>{`ส่งคำขอแบบ ${mode} คน`}</MemberButton>
      </MemberCard>
    </main>
    <BottomNavigation
      active="matches"
      onMonitor={() => navigate('/monitor')}
      onMatches={() => navigate('/match-request')}
      onScan={() => navigate('/profile?scan=1')}
      onReward={() => navigate('/rewards')}
      onProfile={() => navigate('/profile')}
    />
  </MemberPage>;
}

export default MatchRequest;
